import { BusinessUnitsTableDataQuery } from 'src/apps/dashboard/services/queries/business-units-table/BusinessUnitsTable.generated';
import { DashboardDataQuery } from 'src/apps/dashboard/services/queries/dashboard/DashboardData.generated';
import { ScoreDataQuery } from 'src/apps/dashboard/services/queries/score/ScoreData.generated';
import { DashboardWfaSessionsQuery } from 'src/apps/dashboard/services/queries/sessions/Sessions.generated';
import { DashboardWebSdkTestsQuery } from 'src/apps/dashboard/services/queries/Tests/Tests.generated';
import { Logger } from 'src/common/services/Logger';

import demoTenantGraphqlMocks from './demoTenantGraphqlMocks.json';

type GraphqlMockResponse = { data: unknown };

const DEMO_UNIT_FALLBACK = 'Demo Unit';
const DEFAULT_TIMEFRAME_DAYS = 7;

export const DEMO_TENANT_NAME = 'Evinced Dev Team';

const DASHBOARD_DATA_MOCK = demoTenantGraphqlMocks.DashboardData as DashboardDataQuery;
const DASHBOARD_WEB_SDK_TESTS_DATA = demoTenantGraphqlMocks.DashboardWebSdkTests
	?.wsdk_test_runs as DashboardWebSdkTestsQuery['wsdk_test_runs'];
const DASHBOARD_WFA_SESSIONS_DATA = demoTenantGraphqlMocks.DashboardWfaSessions
	?.wfa_session_runs as DashboardWfaSessionsQuery['wfa_session_runs'];
const SCORE_DATA_MOCK = demoTenantGraphqlMocks.ScoreData as ScoreDataQuery;
const BUSINESS_UNITS_TABLE_DATA_MOCK = demoTenantGraphqlMocks.BusinessUnitsTableData as {
	wsdkIssuesCountItem?: { critical_issues_sum: number; serious_issues_sum: number };
	wfaIssuesCountItem?: { critical_issues_sum: number; serious_issues_sum: number };
	scoreWebProductItems?: Array<{
		environment: string;
		timeframe_days: number;
		unique_issues: number;
		score: number;
		calculation_date: string;
	}>;
};

const extractUnitIdsFromCustomFieldsFilter = (filter: unknown): string[] => {
	if (!filter || typeof filter !== 'object') {
		return [];
	}

	const filterValue = (filter as { value?: unknown }).value;
	if (!Array.isArray(filterValue)) {
		return [];
	}

	const unitIds = new Set<string>();

	filterValue.forEach((tupleGroup) => {
		if (!Array.isArray(tupleGroup)) {
			return;
		}

		tupleGroup.forEach((tuple) => {
			if (typeof tuple !== 'string') {
				return;
			}

			const match = tuple.match(/\('unitId','(.+?)'\)/);
			if (match?.[1]) {
				unitIds.add(match[1]);
			}
		});
	});

	return Array.from(unitIds);
};

const resolveUnitIds = (variables?: Record<string, unknown>): string[] => {
	const customFieldsFilter = variables?.customFieldsFilter;
	const unitIdsFromFilter = extractUnitIdsFromCustomFieldsFilter(customFieldsFilter);

	if (unitIdsFromFilter.length > 0) {
		return unitIdsFromFilter;
	}

	const unitIdFilter = variables?.unitIdFilter;
	if (unitIdFilter && typeof unitIdFilter === 'object') {
		const { value } = unitIdFilter as { value?: unknown };
		if (Array.isArray(value) && typeof value[0] === 'string') {
			return [value[0]];
		}
	}

	// Return all demo business units when no filter is applied
	const businessUnits = demoTenantGraphqlMocks.BusinessUnits as string[];
	if (businessUnits && businessUnits.length > 0) {
		// Remove duplicates
		return Array.from(new Set(businessUnits));
	}

	return [DEMO_UNIT_FALLBACK];
};

const sliceByPagination = <T>(
	data: T[] | null | undefined,
	variables?: Record<string, unknown>
): T[] => {
	if (!data) {
		return [];
	}

	const offset = typeof variables?.offset === 'number' ? variables.offset : 0;
	const limit = typeof variables?.limit === 'number' ? variables.limit : data.length;
	return data.slice(offset, offset + limit);
};

/**
 * Helper to check if an item has numeric issue fields
 */
type IssueData = {
	issues?: number | null;
	issues_critical?: number | null;
	issues_seriouse?: number | null;
	unique_issues?: number | null;
	unique_issues_critical?: number | null;
};

const hasIssueFields = (item: unknown): item is IssueData => {
	return typeof item === 'object' && item !== null;
};

/**
 * Scales issue counts based on timeframe ratio
 * Longer timeframes accumulate more issues over their window
 */
const scaleIssueFields = <T extends IssueData>(item: T, scaleFactor: number): T => {
	const scaled = { ...item };

	if (typeof scaled.issues === 'number') {
		scaled.issues = Math.round(scaled.issues * scaleFactor);
	}
	if (typeof scaled.issues_critical === 'number') {
		scaled.issues_critical = Math.round(scaled.issues_critical * scaleFactor);
	}
	if (typeof scaled.issues_seriouse === 'number') {
		scaled.issues_seriouse = Math.round(scaled.issues_seriouse * scaleFactor);
	}
	if (typeof scaled.unique_issues === 'number') {
		scaled.unique_issues = Math.round(scaled.unique_issues * scaleFactor);
	}
	if (typeof scaled.unique_issues_critical === 'number') {
		scaled.unique_issues_critical = Math.round(scaled.unique_issues_critical * scaleFactor);
	}

	return scaled;
};

/**
 * Extracts environment value from GraphQL condition variable
 */
const extractEnvironmentValue = (environmentFilter?: unknown): string | null => {
	if (!environmentFilter || typeof environmentFilter !== 'object') {
		return null;
	}

	const { value } = environmentFilter as { value?: unknown };
	if (!Array.isArray(value) || typeof value[0] !== 'string') {
		return null;
	}

	return value[0];
};

/**
 * Gets environment-specific scale factor
 * Different environments have different amounts of data/issues
 */
const getEnvironmentScaleFactor = (environment: string | null): number => {
	const env = environment?.toLowerCase();
	switch (env) {
		case 'production':
			return 1.0; // Base scale
		case 'staging':
			return 0.7; // 70% of production
		case 'qa':
			return 0.5; // 50% of production
		case 'development':
			return 0.3; // 30% of production
		default:
			return 1.0; // Default to production scale
	}
};

/**
 * Duplicates data for multiple timeframes (7, 30, 90 days) and environments
 * For each timeframe, returns the appropriate number of most recent days with scaled values:
 * - 7 days: last 7 days with base values (scale 1.0)
 * - 30 days: last 30 days with scaled values (scale ~2.5)
 * - 90 days: all available days with scaled values (scale ~5.0)
 *
 * Each environment gets different scale factors:
 * - Production: 100% (base)
 * - Staging: 70%
 * - QA: 50%
 * - Development: 30%
 */
const duplicateForTimeframesAndEnvironments = <
	T extends { timeframe_days?: number; calculation_date?: string; environment?: string }
>(
	data: T[],
	selectedEnvironment?: string | null
): T[] => {
	const timeframes = [
		{ days: 7, dataPoints: 7, timeframeScale: 1.0 },
		{ days: 30, dataPoints: 30, timeframeScale: 2.5 },
		{ days: 90, dataPoints: 90, timeframeScale: 5.0 }
	];

	const environments = ['production', 'staging', 'qa', 'development'];
	const result: T[] = [];

	// Sort data by calculation_date descending (most recent first)
	const sortedData = [...data].sort((a, b) => {
		if (!a.calculation_date || !b.calculation_date) {
			return 0;
		}
		return new Date(b.calculation_date).getTime() - new Date(a.calculation_date).getTime();
	});

	// If environment is selected, only generate data for that environment
	const targetEnvironments = selectedEnvironment
		? [selectedEnvironment.toLowerCase()]
		: environments;

	targetEnvironments.forEach((environment) => {
		const environmentScale = getEnvironmentScaleFactor(environment);

		timeframes.forEach(({ days, dataPoints, timeframeScale }) => {
			// Take the last N data points for this timeframe
			const slicedData = sortedData.slice(0, Math.min(dataPoints, sortedData.length));

			slicedData.forEach((item) => {
				const combinedScale = timeframeScale * environmentScale;

				// Scale issue counts if this item has issue fields
				if (hasIssueFields(item) && combinedScale !== 1.0) {
					const scaledItem = scaleIssueFields({ ...item } as T & IssueData, combinedScale);
					result.push({
						...scaledItem,
						timeframe_days: days,
						environment
					} as T);
				} else {
					result.push({ ...item, timeframe_days: days, environment });
				}
			});
		});
	});

	return result;
};

const createBusinessUnitsTableMock = (
	unitIds: string[],
	days: number,
	environment?: string | null
): BusinessUnitsTableDataQuery => {
	const timeframeDays = days || DEFAULT_TIMEFRAME_DAYS;

	// If no specific environment is requested, generate data for all environments
	// This is needed for BusinessUnitsTable which doesn't filter by environment at the GraphQL level
	const environments = environment
		? [environment.toLowerCase()]
		: ['production', 'staging', 'qa', 'development'];

	// WSDK and WFA counts are aggregated across all environments, so use a blended scale
	const blendedScale = environment ? getEnvironmentScaleFactor(environment) : 1.0;

	const wsdkIssuesCount = unitIds.map((unitId) => ({
		filtered_custom_fields: [unitId],
		critical_issues_sum: Math.round(
			(BUSINESS_UNITS_TABLE_DATA_MOCK?.wsdkIssuesCountItem?.critical_issues_sum ?? 0) * blendedScale
		),
		serious_issues_sum: Math.round(
			(BUSINESS_UNITS_TABLE_DATA_MOCK?.wsdkIssuesCountItem?.serious_issues_sum ?? 0) * blendedScale
		)
	}));

	const wfaIssuesCount = unitIds.map((unitId) => ({
		filtered_custom_fields: [unitId],
		critical_issues_sum: Math.round(
			(BUSINESS_UNITS_TABLE_DATA_MOCK?.wfaIssuesCountItem?.critical_issues_sum ?? 0) * blendedScale
		),
		serious_issues_sum: Math.round(
			(BUSINESS_UNITS_TABLE_DATA_MOCK?.wfaIssuesCountItem?.serious_issues_sum ?? 0) * blendedScale
		)
	}));

	// Generate score_web_products for each environment using the full dataset
	// Use scoreWebProductsDaily which has 31 days of unique issues data
	// Match with ScoreData for scores (normalize dates by removing milliseconds)
	const normalizeDateString = (date: string): string => {
		return date.replace(/\.000Z$/, 'Z').replace(/T00:00:00\.000Z$/, 'T00:00:00Z');
	};

	const scoresMap = new Map(
		(SCORE_DATA_MOCK.score_web_products ?? []).map((item) => [
			normalizeDateString(item.calculation_date),
			item.score
		])
	);

	const scoreWebProductsData = unitIds.flatMap((unitId) =>
		environments.flatMap((env) => {
			const envScale = getEnvironmentScaleFactor(env);
			return (DASHBOARD_DATA_MOCK.scoreWebProductsDaily ?? []).map((item) => {
				const normalizedDate = normalizeDateString(item.calculation_date ?? '');
				const score = scoresMap.get(normalizedDate) ?? 0.75;
				return {
					unit_id: unitId,
					environment: env,
					timeframe_days: timeframeDays,
					unique_issues: Math.round((item.issues ?? 0) * envScale),
					score,
					calculation_date: item.calculation_date
				};
			});
		})
	);

	return {
		wsdkIssuesCount,
		wfaIssuesCount,
		score_web_products: scoreWebProductsData
	};
};

export const getDemoTenantGraphqlMockResponse = (
	operationName: string,
	variables?: Record<string, unknown>
): GraphqlMockResponse | null => {
	Logger.info('Demo tenant mock intercepted', { operationName, variables });

	switch (operationName) {
		case 'DashboardData': {
			// Extract environment filter from variables
			const environment = extractEnvironmentValue(variables?.environmentFilter);

			// For demo tenant, generate data for all timeframes and environments
			return {
				data: {
					...DASHBOARD_DATA_MOCK,
					scoreWebProductsDaily: duplicateForTimeframesAndEnvironments(
						DASHBOARD_DATA_MOCK.scoreWebProductsDaily ?? [],
						environment
					),
					scoreWebProductsDailyTotal: duplicateForTimeframesAndEnvironments(
						DASHBOARD_DATA_MOCK.scoreWebProductsDailyTotal ?? [],
						environment
					),
					scoreWebProducts: duplicateForTimeframesAndEnvironments(
						DASHBOARD_DATA_MOCK.scoreWebProducts ?? [],
						environment
					)
				}
			};
		}
		case 'ScoreData': {
			// Extract environment filter from variables
			const environment = extractEnvironmentValue(variables?.environmentFilter);

			// For demo tenant, generate data for all timeframes and environments
			return {
				data: {
					score_web_products: duplicateForTimeframesAndEnvironments(
						SCORE_DATA_MOCK.score_web_products ?? [],
						environment
					)
				}
			};
		}
		case 'BusinessUnitsTableData': {
			const unitIds = resolveUnitIds(variables);
			const days = typeof variables?.days === 'number' ? variables.days : DEFAULT_TIMEFRAME_DAYS;

			// BusinessUnitsTable doesn't use environmentFilter variable, but we need to
			// return data for all environments with the environment field populated
			// For now, just return ALL the mock data (31 days) and let client-side filter
			// The data is from Jan 12 - Feb 11, 2026
			const mockData = createBusinessUnitsTableMock(unitIds, days, null);

			// Debug logging
			Logger.info('BusinessUnitsTableData mock called', {
				unitIds,
				days,
				wsdkCount: mockData.wsdkIssuesCount?.length,
				wfaCount: mockData.wfaIssuesCount?.length,
				scoreProductsCount: mockData.score_web_products?.length,
				sampleScoreProduct: mockData.score_web_products?.[0],
				dateRange: {
					first: mockData.score_web_products?.[0]?.calculation_date,
					last: mockData.score_web_products?.[mockData.score_web_products.length - 1]
						?.calculation_date
				},
				requestedDateRange: {
					from: variables?.calculationDateFrom,
					to: variables?.calculationDateTo
				}
			});

			return { data: mockData };
		}
		case 'DashboardWebSdkTests': {
			const rows = sliceByPagination(DASHBOARD_WEB_SDK_TESTS_DATA ?? [], variables);
			const totalCount = DASHBOARD_WEB_SDK_TESTS_DATA?.length ?? 0;
			return {
				data: {
					wsdk_test_runs: rows,
					total: [{ run_id_count: totalCount }],
					unfiltered_total: [{ run_id_count: totalCount }]
				}
			};
		}
		case 'DashboardWfaSessions': {
			const rows = sliceByPagination(DASHBOARD_WFA_SESSIONS_DATA ?? [], variables);
			const totalCount = DASHBOARD_WFA_SESSIONS_DATA?.length ?? 0;
			return {
				data: {
					wfa_session_runs: rows,
					total: [{ run_id_count: totalCount }],
					unfiltered_total: [{ run_id_count: totalCount }]
				}
			};
		}
		default:
			return null;
	}
};
